import {Form, Formik, FormikProps} from 'formik';
import {map, isEmpty, first, includes, filter, forEach, some} from 'lodash';
import {Fragment, useContext, useMemo, useEffect, useState, useCallback} from 'react';
import {useTranslation} from 'react-i18next';
import {RouteComponentProps} from 'react-router-dom';

import {groupApi, rolesApi, userApi, registrationApi, accountApi} from 'api';
import {
    PageHeader, InputFormik, PhoneInputFormik, MultiInputFormik, AssignmentSelectorsFormik,
    ToggleFormik, SelectFormik, Toggle, Button, PredefinedNotifierType, UnsavedChangesLeavePrompt,
    PasswordInputFormik, ConfirmModal, PasswordChangedAt
} from 'components';
import {ROUTE_PATH} from 'constants/routes';
import {PROFILE_FIELD} from 'constants/user';
import {UserContext} from 'context';
import {history} from 'context/history';
import {ValidationCode} from 'enums';
import {useApiData, useObservable} from 'hooks';
import {Group, UserGroup, AccountAssignmentInfo} from 'interfaces';
import {TRANSLATION_KEYS} from 'locale/translation-keys';
import {authDataStore} from 'services';
import {Account, ApiError, BaseUserRole, MFAStatus, RolePermission, UserParticipantRole, User, UserRole} from 'types';
import {apiUtils, dataUtils, logger, userAuthUtils} from 'utils';
import {getInSessionRoleName, getRoleName} from 'utils/transform';

const FORM_CONTROL_T_KEYS = TRANSLATION_KEYS.COMMON.FORM_CONTROL;
const T_KEYS = TRANSLATION_KEYS.USER.ACCOUNT.PROFILE;
const SESSION_T_KEYS = TRANSLATION_KEYS.COMMON.SESSION.LABELS;

const OVERRIDES = {
    [PROFILE_FIELD.EMAIL]: {
        [ValidationCode.Duplicated]: T_KEYS.ERRORS.EMAIL.DUPLICATE
    },
    [PROFILE_FIELD.LOGIN]: {
        [ValidationCode.Duplicated]: T_KEYS.ERRORS.LOGIN.DUPLICATE
    },
    [PROFILE_FIELD.OLD_PASSWORD]: {
        [ValidationCode.Invalid]: T_KEYS.ERRORS.OLD_PASSWORD.INVALID
    },
    [PROFILE_FIELD.PASSWORD]: {
        [ValidationCode.Invalid]: T_KEYS.ERRORS.PASSWORD.WEAK,
        [ValidationCode.Duplicated]: T_KEYS.ERRORS.PASSWORD.DUPLICATE,
        [ValidationCode.InvalidFormat]: T_KEYS.ERRORS.PASSWORD.INVALID_FORMAT,
        [ValidationCode.MinLength]: T_KEYS.ERRORS.PASSWORD.MIN_LENGTH,
        [ValidationCode.MaxLength]: T_KEYS.ERRORS.PASSWORD.MAX_LENGTH,
    },
    [PROFILE_FIELD.CONFIRM_PASSWORD]: {
        [ValidationCode.MinLength]: T_KEYS.ERRORS.PASSWORD.MIN_LENGTH,
        [ValidationCode.Invalid]: T_KEYS.ERRORS.CONFIRM_PASSWORD.INVALID
    }
};

type PasswordFields = {
    changePassword: boolean;
    password?: string;
    oldPassword?: string;
    confirmPassword?: string;
}

type FormFields = Partial<User> & PasswordFields & {
    accountId: string;
    mfaEmail: {
        enabled: boolean;
    };
    roles: UserRole[];
    inSessionRoles: UserParticipantRole[];
    groups: AccountAssignmentInfo[];
}

enum RegistrationType {
    Password = 'password',
    RegistrationLink = 'registrationLink'
}

type FormTemplateProps = {
    loading: boolean;
    error: ApiError;
    registrationType: RegistrationType;
    mfaStatus: MFAStatus;
    isCreating: boolean;
    isEditing: boolean;
    withPasswordAllowed: boolean;
    useScreenRecorder: boolean;
    hasManagePermissions: boolean;
    isAppAdmin: boolean;
    roles: RolePermission[];
    assistantEnabled: boolean;
    groups: Group[];
    accounts: Account[];
    canChangeRegistrationType: boolean;
    canChangePassword: boolean;
    isPersonalProfile: boolean;
    activated: boolean;
    user: User;
    onSendRegistrationLinkChange: (checked: boolean) => void;
    onSendRegistrationLinkEmail: () => void;
} & FormikProps<FormFields>;

const FormTemplate = ({
    loading, error, values: formValues, initialValues, registrationType, isCreating, isEditing, withPasswordAllowed, useScreenRecorder,
    hasManagePermissions, isAppAdmin, roles, groups, accounts, canChangeRegistrationType, canChangePassword, isPersonalProfile, activated,
    user, errors, mfaStatus, assistantEnabled, setFieldError, onSendRegistrationLinkChange, onSendRegistrationLinkEmail
}: FormTemplateProps) => {
    const {t} = useTranslation();

    const errorsFormatted = useMemo(() => apiUtils.getFieldsValidationErrorMessages(error, t, map(PROFILE_FIELD, (value) => ({
        field: value,
        overrides: OVERRIDES[value]
    }))), [error, t]);

    const isValidGroupId = useCallback((groupId) => some(groups, {id: groupId}), [groups]);

    const hasNotSelectedGroup = useMemo(() => some(formValues.groups,
        (selectedGroup) => !isValidGroupId(selectedGroup.groupId)), [formValues.groups, isValidGroupId]);

    const getGroupError = useCallback((item: AccountAssignmentInfo, selectedItems: AccountAssignmentInfo[]) => {
        if (item?.groupId && item?.subgroupId) {
            return null;
        } else if (item?.groupId && !item.subgroupId &&
            filter(selectedItems, (selectedItem) => selectedItem.groupId === item.groupId && !selectedItem.subgroupId).length > 1
        ) {
            return {code: ValidationCode.Duplicated};
        } else if (!item?.groupId || !isValidGroupId(item.groupId)) {
            return {code: ValidationCode.Required};
        }
    }, [isValidGroupId]);

    const getSubgroupError = useCallback((item: AccountAssignmentInfo, selectedItems: AccountAssignmentInfo[]) => {
        if (item?.subgroupId && filter(selectedItems, {groupId: item?.groupId, subgroupId: item?.subgroupId}).length > 1) {
            return {code: ValidationCode.Duplicated};
        } else if (!item?.subgroupId &&
            filter(selectedItems, (selectedItem) => selectedItem.groupId === item?.groupId && selectedItem.subgroupId).length
        ) {
            return {code: ValidationCode.Required};
        }
    }, []);

    const availableRoles = useMemo(() => [
        {id: BaseUserRole.Agent, name: BaseUserRole.Agent},
        {id: BaseUserRole.Admin, name: BaseUserRole.Admin},
        ...(roles || [])
    ], [roles]);

    const rolesOptions = useMemo(() =>
        map(availableRoles, ({id}) => ({id, name: getRoleName(t, id, availableRoles)})), [t, availableRoles]);

    const inSessionRolesOptions = useMemo(() =>
        map([UserParticipantRole.Agent, UserParticipantRole.Assistant],
            (inSessionRole) => ({id: inSessionRole, name: getInSessionRoleName(t, inSessionRole)})), [t]);

    const accountsOptions = useMemo(() =>
        map(accounts, (account) => ({id: account.id, name: account.name})), [accounts]);

    const hasGroupOrSubgroupError = useMemo(() =>
        some(formValues.groups, (group) => getGroupError(group, formValues.groups) || getSubgroupError(group, formValues.groups)),
    [formValues.groups, getGroupError, getSubgroupError]);

    const isSaveDisabled = useMemo<boolean>(() =>
        hasGroupOrSubgroupError || !isEmpty(errors) ||
        dataUtils.isEqualValues(formValues, initialValues, true) || loading || hasNotSelectedGroup,
    [hasGroupOrSubgroupError, errors, formValues, initialValues, loading, hasNotSelectedGroup]);

    useEffect(() => {
        forEach(errorsFormatted, (value, key) => value && setFieldError(key, value));
    }, [setFieldError, errorsFormatted]);

    return (
        <Form>
            {isAppAdmin && isEditing &&
                <div className='tw-grid tw-mb-6 lg:tw-grid-cols-3 tw-gap-3 lg:tw-gap-6 lg:tw-mr-10'>
                    <SelectFormik
                        name={PROFILE_FIELD.ACCOUNT}
                        placeholder={t(T_KEYS.FORM_CONTROLS.ACCOUNT)}
                        options={accountsOptions}
                        valueAs='id'
                        required
                    />
                </div>}
            <div className='tw-grid lg:tw-grid-cols-3 tw-gap-3 lg:tw-gap-6 lg:tw-mr-10'>
                <InputFormik name={PROFILE_FIELD.FIRST_NAME} label={t(T_KEYS.FORM_CONTROLS.FIRST_NAME)} required />
                <InputFormik name={PROFILE_FIELD.MIDDLE_NAME} label={t(T_KEYS.FORM_CONTROLS.MIDDLE_NAME)} />
                <InputFormik name={PROFILE_FIELD.LAST_NAME} label={t(T_KEYS.FORM_CONTROLS.LAST_NAME)} required />
            </div>
            <div className='tw-grid lg:tw-grid-cols-2 tw-gap-6 lg:tw-mr-10 tw-mt-6'>
                <InputFormik
                    name={PROFILE_FIELD.EMAIL}
                    label={t(T_KEYS.FORM_CONTROLS.EMAIL)}
                    description={
                        (registrationType === RegistrationType.RegistrationLink || (isCreating && !withPasswordAllowed)) ?
                            t(T_KEYS.MESSAGES.USER_WILL_GET_EMAIL) : null
                    }
                    disabled={isEditing}
                    required={isCreating}
                />
                <InputFormik name={PROFILE_FIELD.TITLE} label={t(T_KEYS.FORM_CONTROLS.TITLE)} />
                <PhoneInputFormik name={PROFILE_FIELD.PHONE} label={t(T_KEYS.FORM_CONTROLS.PHONE_NUMBER)} />
                <InputFormik name={PROFILE_FIELD.SKYPE} label={t(T_KEYS.FORM_CONTROLS.SKYPE)} />
                {useScreenRecorder &&
                    <InputFormik
                        name={PROFILE_FIELD.LOGIN}
                        label={t(T_KEYS.FORM_CONTROLS.MACHINE_NAME)}
                        description={t(T_KEYS.DESCRIPTIONS.MACHINE_NAME)}
                        disabled={!hasManagePermissions}
                        required
                    />}
                {hasManagePermissions &&
                    <Fragment>
                        <SelectFormik
                            name={PROFILE_FIELD.ROLES}
                            placeholder={t(T_KEYS.FORM_CONTROLS.ROLES)}
                            options={rolesOptions}
                            multiple
                            required
                            hasError={!!errorsFormatted[PROFILE_FIELD.ROLES]}
                            valueAs='id'
                        />
                        {assistantEnabled &&
                            <SelectFormik
                                name={PROFILE_FIELD.IN_SESSION_ROLES}
                                placeholder={t(T_KEYS.FORM_CONTROLS.IN_SESSION_ROLES)}
                                options={inSessionRolesOptions}
                                multiple
                                required
                                hasError={!!errorsFormatted[PROFILE_FIELD.IN_SESSION_ROLES]}
                                valueAs='id'
                            />}
                    </Fragment>}
            </div>
            <div className='tw-mt-10'>
                <div className='tw-font-bold tw-mb-2 lg:tw-hidden'>{t(SESSION_T_KEYS.ASSIGNMENT)}</div>
                <MultiInputFormik
                    name={PROFILE_FIELD.GROUPS}
                    itemsPath={PROFILE_FIELD.GROUPS}
                    values={formValues}
                    isValid={(item) => item?.groupId}
                >
                    {({index, item, path, last, renderActions}) => (
                        <div key={index} className='tw-flex tw-items-start lg:tw-items-center tw-mb-6 lg:tw-mb-3'>
                            <AssignmentSelectorsFormik
                                disabled={!hasManagePermissions}
                                groupError={!last && getGroupError(item, formValues.groups)}
                                subgroupError={!last && getSubgroupError(item, formValues.groups)}
                                name={path}
                                groups={groups}
                            >
                                {({renderGroup, renderSubgroup}) => (
                                    <div className='tw-w-full tw-grid lg:tw-grid-cols-2 lg:tw-gap-6'>
                                        {renderGroup()}
                                        {renderSubgroup()}
                                    </div>
                                )}
                            </AssignmentSelectorsFormik>
                            <div className='tw-w-10 tw-mt-6 lg:tw-mt-0'>{hasManagePermissions && renderActions({index, item})}</div>
                        </div>
                    )}
                </MultiInputFormik>
            </div>
            {canChangeRegistrationType &&
                <div className='tw-flex tw-justify-between tw-mt-10 lg:tw-mr-10'>
                    <Toggle
                        name='sendRegistrationLink'
                        label={t(T_KEYS.FORM_CONTROLS.SEND_REGISTRATION_LINK)}
                        labelPlacement='right'
                        checked={registrationType === RegistrationType.RegistrationLink}
                        onChange={onSendRegistrationLinkChange}
                    />
                </div>}
            {isPersonalProfile && (mfaStatus === MFAStatus.Optional || mfaStatus === MFAStatus.Required) &&
                <ToggleFormik
                    className='tw-mt-4'
                    disabled={mfaStatus === MFAStatus.Required}
                    name='mfaEmail.enabled'
                    label={t(T_KEYS.FORM_CONTROLS.MFA_EMAIL)}
                    labelPlacement='right'
                />}
            {canChangePassword &&
                <div className='tw-mt-4 lg:tw-mr-10'>
                    <div>
                        {(isPersonalProfile || isEditing) &&
                            <Fragment>
                                <ToggleFormik
                                    name='changePassword'
                                    label={t(T_KEYS.FORM_CONTROLS.CHANGE_PASSWORD)}
                                    labelPlacement='right'
                                />
                                <PasswordChangedAt user={user} className='tw-my-2' />
                            </Fragment>}
                        {(
                            formValues.changePassword || isCreating && registrationType === RegistrationType.Password
                        ) && (
                            <div>
                                {(isPersonalProfile || (isEditing && activated)) &&
                                    <PasswordInputFormik
                                        required
                                        name={PROFILE_FIELD.OLD_PASSWORD}
                                        label={t(T_KEYS.FORM_CONTROLS.OLD_PASSWORD)}
                                    />}
                                {(isPersonalProfile || isEditing || registrationType === RegistrationType.Password) &&
                                    <div className='tw-grid lg:tw-grid-cols-2 tw-gap-3 lg:tw-gap-6 tw-mt-4'>
                                        <PasswordInputFormik
                                            required
                                            name={PROFILE_FIELD.PASSWORD}
                                            autoComplete='new-password'
                                            label={t(FORM_CONTROL_T_KEYS.PASSWORD)}
                                        />

                                        <PasswordInputFormik
                                            required
                                            name={PROFILE_FIELD.CONFIRM_PASSWORD}
                                            label={t(FORM_CONTROL_T_KEYS.CONFIRM_PASSWORD)}
                                            autoComplete='new-password'
                                        />
                                    </div>}
                            </div>
                        )}
                    </div>
                </div>}

            <div className='tw-text-right tw-mt-8 lg:tw-mr-10'>
                {!activated && isEditing &&
                    <Button
                        type='button'
                        color='default'
                        disabled={loading}
                        onClick={onSendRegistrationLinkEmail}
                    >
                        {t(T_KEYS.BUTTONS.SEND_REGISTRATION_LINK)}
                    </Button>}
                <Button
                    type='submit'
                    color='primary'
                    className='tw-ml-4'
                    disabled={isSaveDisabled}
                >
                    {t(T_KEYS.BUTTONS.SAVE)}
                </Button>
            </div>
            <UnsavedChangesLeavePrompt values={formValues} initialValues={initialValues} />
        </Form>
    );
};

type Props = {
    isPersonalProfile: boolean;
} & RouteComponentProps<{eid: string}>;

export const Profile = ({match, isPersonalProfile}: Props) => {
    const [t] = useTranslation();
    const {settings} = useContext(UserContext);
    const mfaStatus = useMemo(() => settings.security?.mfa?.status, [settings]);
    const [registrationType, setRegistrationType] = useState<RegistrationType>();
    const [submittedRegistrationType, setSubmittedRegistrationType] = useState<RegistrationType>();
    const [originalUser, setOriginalUser] = useState<Partial<User>>();
    const [initialPasswordFields, setInitialPasswordFields] = useState<PasswordFields>({
        changePassword: false
    });
    const resetInitialPasswordFields = useCallback(() => {
        setInitialPasswordFields({
            changePassword: false,
            oldPassword: '',
            password: '',
            confirmPassword: ''
        });
    }, [setInitialPasswordFields]);
    const {localUser} = useObservable(authDataStore);
    const [accountChangeConfirmationModalProps, setAccountChangeConfirmationModalProps] = useState<{
        open: boolean,
        data?: FormFields
    }>({open: false});

    const refreshPage = useCallback(() => {
        window.location.reload();
    }, []);

    const {data: user, send: requestUser} = useApiData<User, [number]>(userApi.getUser);

    const goToUsers = useCallback(() => history.push(ROUTE_PATH.USER.ACCOUNT.USERS), []);
    const handleSuccessAction = useCallback((userPayload: User) => {
        if (userPayload) {
            setOriginalUser(userPayload);
            resetInitialPasswordFields();
            if (userPayload.accountId !== localUser.accountId && isPersonalProfile) {
                refreshPage();
            }
        }
        if (!isPersonalProfile) {
            // add some delay to allow finish rendering
            setTimeout(() => {
                goToUsers();
            }, 500);
        } else {
            requestUser(userPayload.eid);
        }
    }, [isPersonalProfile, resetInitialPasswordFields, localUser.accountId, refreshPage, goToUsers, requestUser]);

    const handleSendRegistrationLink = useCallback(() => {
        handleSuccessAction(null);
    }, [handleSuccessAction]);

    const {send: sendRequestRegistrationLinkEmail, loading: sendRequestRegistrationLinkEmailLoading} = useApiData(
        registrationApi.sendRegistrationLink, {onSuccess: handleSendRegistrationLink, notificationType: PredefinedNotifierType.Send}
    );
    const {
        send: createUserWithEmailConfirmation,
        error: createUserWithEmailConfirmationError,
        loading: createUserWithEmailConfirmationLoading
    } = useApiData<User, [User]>(userApi.createUserWithEmailConfirmation,
        {notificationType: PredefinedNotifierType.Save, onSuccess: handleSuccessAction});
    const {
        send: createUserWithPassword,
        error: createUserWithPasswordError,
        loading: createUserWithPasswordLoading
    } = useApiData<User, [User]>(userApi.createUserWithPassword,
        {notificationType: PredefinedNotifierType.Save, onSuccess: handleSuccessAction});
    const {
        send: updateUser,
        error: updateUserWithPasswordError,
        loading: updateUserLoading
    } = useApiData<User, [number, User]>(userApi.updateUser,
        {notificationType: PredefinedNotifierType.Save, onSuccess: handleSuccessAction});

    const {data: groups, send: requestGroups} = useApiData<Group[]>(groupApi.getGroups);
    const {data: roles, send: requestRoles} = useApiData<RolePermission[]>(rolesApi.getRolesPermissions);
    const {data: accounts, send: requestAccounts} = useApiData<Account[]>(accountApi.getAccounts);

    const permissions = useMemo(() => userAuthUtils.getUserPermissions(), []);
    const hasManagePermissions = useMemo(() => permissions.account?.users?.currentInfo?.enabled, [permissions]);
    const eid = useMemo(() => {
        let userEid = Number(match.params.eid);
        if (isPersonalProfile) {
            if (!localUser || !localUser.eid) {
                logger.error('User data not found');
                goToUsers();
            }
            userEid = localUser.eid;
        } else if (userEid && !hasManagePermissions) {
            logger.error('No permissions to update user account');
            goToUsers();
        }
        return userEid;
    }, [match, isPersonalProfile, hasManagePermissions, localUser, goToUsers]);
    const isEditing = useMemo(() => !!eid, [eid]);
    const isCreating = useMemo(() => !isEditing, [isEditing]);
    const activated = useMemo(() => originalUser?.activated || false, [originalUser]);

    const [title, description] = useMemo(() => {
        if (isPersonalProfile) {
            return [t(T_KEYS.TITLES.YOUR_PROFILE), t(T_KEYS.DESCRIPTIONS.YOUR_PROFILE)];
        } else if (isCreating) {
            return [t(T_KEYS.TITLES.CREATE_PROFILE), t(T_KEYS.DESCRIPTIONS.CREATE_PROFILE)];
        } else {
            return [t(T_KEYS.TITLES.USER_PROFILE), t(T_KEYS.DESCRIPTIONS.USER_PROFILE)];
        }
    }, [t, isPersonalProfile, isCreating]);

    const useScreenRecorder = useMemo(() => settings.featuresAvailability.screenRecorder, [settings]);
    const withPasswordAllowed = useMemo(() => settings.registration.withPassword, [settings]);
    const withEmailConfirmationAllowed = useMemo(() => settings.registration.withEmailConfirmation, [settings]);
    const availableRoles = useMemo(() => [
        {id: BaseUserRole.Agent, name: BaseUserRole.Agent},
        {id: BaseUserRole.Admin, name: BaseUserRole.Admin},
        ...(roles || [])
    ], [roles]);
    const canChangeRegistrationType = useMemo(() => isCreating && withPasswordAllowed && withEmailConfirmationAllowed,
        [isCreating, withPasswordAllowed, withEmailConfirmationAllowed]);
    const canChangePassword = useMemo(() => withPasswordAllowed || isPersonalProfile, [isPersonalProfile, withPasswordAllowed]);

    const userRolesOptions = useMemo(() => {
        const availableRoleIds = map(availableRoles, 'id');
        return map(filter(originalUser?.roles, (roleId) => includes(availableRoleIds, roleId)),
            (roleId) => ({id: roleId, name: getRoleName(t, roleId, availableRoles)}));
    }, [t, originalUser, availableRoles]);
    const groupsValueToDto = useCallback((data: AccountAssignmentInfo[]): UserGroup[] => {
        if (!Array.isArray(data)) {
            return [];
        }
        const groupsMap = {};
        data.forEach(({groupId, subgroupId}) => {
            if (!groupsMap[groupId]) {
                groupsMap[groupId] = [];
            }
            if (subgroupId && !includes(groupsMap[groupId], subgroupId)) {
                groupsMap[groupId].push(subgroupId);
            }
        });
        return Object.keys(groupsMap).map((groupId) => ({
            id: groupId,
            subgroups: groupsMap[groupId].map((id) => ({id}))
        }));
    }, []);

    const groupsDtoToValue = useCallback((data: UserGroup[]) => {
        if (!Array.isArray(data)) {
            return [];
        }
        const results: AccountAssignmentInfo[] = [];
        data.forEach(({id: groupId, subgroups}) => {
            if (Array.isArray(subgroups) && subgroups.length) {
                subgroups.forEach(({id: subgroupId}) => {
                    results.push({groupId, subgroupId});
                });
            } else {
                results.push({groupId});
            }
        });
        return results;
    }, []);

    const userGroups = useMemo(() => groupsDtoToValue(originalUser?.groups), [originalUser, groupsDtoToValue]);

    const onSendRegistrationLinkChange = useCallback((useRegistrationLink: boolean) => {
        const newType = useRegistrationLink ?
            RegistrationType.RegistrationLink :
            RegistrationType.Password;
        setRegistrationType(newType);
    }, []);

    const sendRegistrationLinkEmail = useCallback(() => {
        sendRequestRegistrationLinkEmail(originalUser.email);
    }, [originalUser, sendRequestRegistrationLinkEmail]);

    const isAppAdmin = useMemo(() => userAuthUtils.isAppAdmin(localUser), [localUser]);

    const mfaEmailEnabled = useMemo(() => mfaStatus === MFAStatus.Required || !!user?.mfaEmail?.enabled, [mfaStatus, user]);

    const initialValues = useMemo<FormFields>(() => ({
        ...originalUser,
        ...initialPasswordFields,
        mfaEmail: {
            enabled: mfaEmailEnabled
        },
        roles: map(userRolesOptions, 'id'),
        groups: userGroups
    } as FormFields),
    [originalUser, userRolesOptions, userGroups, initialPasswordFields, mfaEmailEnabled]);

    const submitData = useCallback((data: User) => {
        if (isEditing) {
            updateUser(originalUser.eid, data);
            return;
        }
        if (registrationType === RegistrationType.RegistrationLink) {
            createUserWithEmailConfirmation(data);
        } else {
            createUserWithPassword(data);
        }
        setSubmittedRegistrationType(registrationType);
    }, [isEditing, registrationType, updateUser, originalUser?.eid, createUserWithEmailConfirmation, createUserWithPassword]);

    const error = useMemo(() => {
        if (isEditing) {
            return updateUserWithPasswordError;
        }
        if (submittedRegistrationType === RegistrationType.RegistrationLink) {
            return createUserWithEmailConfirmationError;
        }
        return createUserWithPasswordError;
    }, [updateUserWithPasswordError, createUserWithEmailConfirmationError, createUserWithPasswordError,
        isEditing, submittedRegistrationType]);

    const loading = useMemo(() =>
        createUserWithEmailConfirmationLoading ||
        createUserWithPasswordLoading ||
        updateUserLoading ||
        sendRequestRegistrationLinkEmailLoading,
    [createUserWithEmailConfirmationLoading, createUserWithPasswordLoading, updateUserLoading, sendRequestRegistrationLinkEmailLoading]);

    const prepareSubmitData = useCallback((formValues: FormFields) => {
        const data: any = {
            firstName: formValues.firstName,
            middleName: formValues.middleName,
            lastName: formValues.lastName,
            title: formValues.title,
            phone: formValues.phone,
            skype: formValues.skype,
        };
        if (!isEditing) {
            data.email = formValues.email;
        }
        if (mfaStatus === MFAStatus.Optional && isPersonalProfile) {
            data.mfaEmail = formValues.mfaEmail;
        }
        if (hasManagePermissions) {
            data.groups = groupsValueToDto(formValues.groups);
            data.roles = formValues.roles;
            data.inSessionRoles = formValues.inSessionRoles;
            if (!settings.featuresAvailability.assistantEnabled) {
                data.inSessionRoles = [UserParticipantRole.Agent];
            }
            if (includes(originalUser.roles, BaseUserRole.AppAdmin)) {
                data.roles.push(BaseUserRole.AppAdmin);
            }
            if (isAppAdmin) {
                data.accountId = formValues.accountId;
            }
            if (useScreenRecorder && formValues.login) {
                data.login = formValues.login;
            }
        }
        if (formValues.changePassword) {
            data.oldPassword = formValues.oldPassword;
        }
        if (formValues.changePassword || !isEditing && registrationType === RegistrationType.Password) {
            data.password = formValues.password;
            data.confirmPassword = formValues.confirmPassword;
        }
        return data;
    }, [
        isEditing, hasManagePermissions, registrationType, mfaStatus, groupsValueToDto,
        isPersonalProfile, originalUser, isAppAdmin, useScreenRecorder, settings.featuresAvailability.assistantEnabled
    ]);

    const handleSubmit = useCallback((formValues: FormFields) => {
        if (formValues.accountId !== initialValues.accountId && isPersonalProfile) {
            setAccountChangeConfirmationModalProps({open: true, data: formValues});
        } else {
            const data = prepareSubmitData(formValues);
            submitData(data);
        }
    }, [initialValues.accountId, isPersonalProfile, prepareSubmitData, submitData]);

    const handleConfirmAccountChangeModal = useCallback((confirm: boolean, data: FormFields) => {
        if (confirm) {
            submitData(prepareSubmitData(data));
        }
        setAccountChangeConfirmationModalProps({open: false});
    }, [prepareSubmitData, submitData]);

    useEffect(() => {
        if (isEditing) {
            requestUser(eid);
        } else {
            setOriginalUser({
                login: '',
                firstName: '',
                middleName: '',
                lastName: '',
                title: '',
                phone: '',
                skype: '',
                email: '',
                oldPassword: '',
                password: '',
                confirmPassword: '',
                inSessionRoles: [UserParticipantRole.Agent],
            });
        }
    }, [isEditing, eid, requestUser]);

    useEffect(() => {
        if (user) {
            setOriginalUser(user);
        }
    }, [user, groupsDtoToValue]);

    useEffect(() => {
        if (isAppAdmin) {
            requestAccounts();
        }
    }, [isAppAdmin, requestAccounts]);

    useEffect(() => {
        requestGroups();
        if (hasManagePermissions) {
            requestRoles();
        }
    }, [hasManagePermissions, requestGroups, requestRoles]);

    useEffect(() => {
        if (isEditing) {
            return;
        }
        const registrationTypes = [];
        if (withPasswordAllowed) {
            registrationTypes.push(RegistrationType.Password);
        }
        if (withEmailConfirmationAllowed) {
            registrationTypes.push(RegistrationType.RegistrationLink);
        }
        if (isEmpty(registrationTypes)) {
            logger.error('No user creating mechanism is defined');
            goToUsers();
        }
        setRegistrationType(first(registrationTypes));
    }, [isEditing, withPasswordAllowed, withEmailConfirmationAllowed, goToUsers]);

    if (!originalUser) {
        return null;
    }

    return (
        <Fragment>
            <PageHeader
                title={title}
                description={description}
            />
            <Formik
                initialValues={initialValues}
                enableReinitialize
                onSubmit={handleSubmit}
            >
                {(props) => (
                    <FormTemplate
                        {...props}
                        loading={loading}
                        error={error}
                        mfaStatus={mfaStatus}
                        registrationType={registrationType}
                        isCreating={isCreating}
                        isEditing={isEditing}
                        withPasswordAllowed={withPasswordAllowed}
                        useScreenRecorder={useScreenRecorder}
                        hasManagePermissions={hasManagePermissions}
                        isAppAdmin={isAppAdmin}
                        roles={roles}
                        assistantEnabled={settings.featuresAvailability.assistantEnabled}
                        groups={groups}
                        accounts={accounts}
                        canChangeRegistrationType={canChangeRegistrationType}
                        canChangePassword={canChangePassword}
                        isPersonalProfile={isPersonalProfile}
                        activated={activated}
                        user={user}
                        onSendRegistrationLinkEmail={sendRegistrationLinkEmail}
                        onSendRegistrationLinkChange={onSendRegistrationLinkChange}
                    />
                )}
            </Formik>
            <ConfirmModal
                maxWidth='xs'
                {...accountChangeConfirmationModalProps}
                title={t(T_KEYS.ACCOUNT_CHANGE_MODAL.TITLE)}
                content={t(T_KEYS.ACCOUNT_CHANGE_MODAL.DESCRIPTION)}
                onClose={handleConfirmAccountChangeModal}
            />
        </Fragment>
    );
};
